import {weakMemo} from '@packages/utilities/memo';
import {pair} from '@packages/utilities/Pair';
import * as KeyMap from '@packages/utilities/key-map';
import * as KeySet from '@packages/utilities/key-set';
import {
  EVariable,
  ESelect,
  EList,
  ERecord,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  Component,
  Expressions,
  Variable,
  ExpressionPath,
  matchExpression,
} from '@packages/systems/wfdl';
import {
  getName,
  identifier,
  type Qualified,
} from '@packages/systems/wfdl-Qualified';
import {generateGuid} from '@packages/utilities/guid';
import {updateElementData} from '@packages/utilities/platform-elementdata';
import {EElement} from '@packages/systems/wfdl-ast';
import {at, toSelectPath, call} from '@packages/utilities/wfdl-helpers';
import {
  constant,
  constantTrue,
  identity,
  emptyObject,
} from '@packages/utilities/fp/utils';
import {deconstructAppend, constructAppend} from '@packages/utilities/append';

const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

const contextVariable = Variable.unqualified(identifier('context'));
const isContextVariable = Variable.equals(contextVariable);

const LOCALIZATION_CONTEXT_KEY = 'localizations';

export function generateLocalizationKey(): string {
  return generateGuid(6);
}

export function createLocalizationBinding(
  key: string,
  componentId: string
): Expression {
  return at(EVariable(contextVariable), [
    LOCALIZATION_CONTEXT_KEY,
    componentId,
    key,
  ]);
}

export type LocalizationBindingData = {
  componentId: string;
  key: string;
};

export function getLocalizationBindingFromSelect(
  expr: Expression
): LocalizationBindingData | null {
  const {
    from,
    path: [p1, p2, p3],
  } = toSelectPath(expr);

  if (p1 !== LOCALIZATION_CONTEXT_KEY) {
    return null;
  }

  if (!p2 || !p3) return null;

  const maybeVariable = Expressions.getVariable(from);
  if (!maybeVariable || !isContextVariable(maybeVariable)) {
    return null;
  }

  return {
    componentId: p2,
    key: p3,
  };
}

/**
 * Maps unqualified variable names to the set of used properties from that
 * variable if the variable is a Record. An empty set means the whole variable
 * is used.
 */
export type LocalizationScope = KeyMap.Type<KeySet.Type>;
const emptyScope: LocalizationScope = emptyObject;

const unionScope: (
  s1: LocalizationScope
) => (s2: LocalizationScope) => LocalizationScope = KeyMap.unionWith(
  KeyMap.union
);

export const equalScope: (
  s1: LocalizationScope,
  s2: LocalizationScope
) => boolean = KeyMap.equal(KeyMap.equal(constantTrue));

const scopeToArgs = (scope: LocalizationScope) =>
  Object.keys(scope)
    .sort()
    .map((key) => {
      const props = scope[key]!;
      const keys = Object.keys(props);
      const from = EVariable(Variable.unqualified(key));
      if (keys.length < 1) return from;
      return ERecord(
        Object.keys(props).reduce((subRec: {[k: string]: Expression}, prop) => {
          subRec[prop] = ESelect({from, prop});
          return subRec;
        }, {})
      );
    });

type SeenVars = KeySet.Type;
const noSeenVars: SeenVars = emptyObject;

const scopeUnchanged = (context: SeenVars) => pair(context, emptyScope);

const withScope = (scope: LocalizationScope) => (context: SeenVars) =>
  pair(context, scope);

const giveNull = <a>(_: a) => null;

const getVar = Variable.match({
  unqualified: identity,
  qualified: giveNull,
});

const getVarFromExpr = matchExpression({
  Variable: getVar,
  _: giveNull,
});

export const buildLocalizationScope = Expressions.everythingWithContext(
  noSeenVars
)(unionScope)(
  matchExpression({
    Variable: (v) => {
      const id = getVar(v);
      if (id == null) return scopeUnchanged;
      return withScope({[id]: KeySet.empty()});
    },
    Select: ({from, prop}) => {
      const id = getVarFromExpr(from);
      if (id == null) return scopeUnchanged;
      return withScope({[id]: KeySet.singleton(prop)});
    },
    Function:
      ({param}) =>
      (context: SeenVars) =>
        pair(KeySet.add(context, param), emptyScope),
    _: constant(scopeUnchanged),
  })
);

export const getLocalizationInfoFromAppend = weakMemo((expr: Expression) => {
  const parts = deconstructAppend(expr);
  const isAppend = parts.length > 1;
  if (!isAppend) return null;
  for (const part of parts) {
    const info = getLocalizationInfoFromExpression(part);
    if (info) return info;
  }
  return null;
});

export const getLocalizationInfoFromExpression = weakMemo(
  (expr: Expression) => {
    // Assume we don’t support localizing a function in a function call
    const {fun, args} = Expressions.deconstructCall(expr);
    const binding = getLocalizationBindingFromSelect(fun);
    if (!binding) return null;
    const scope = buildLocalizationScope(EList(args));
    return {componentId: binding.componentId, key: binding.key, scope};
  }
);

/**
 * Static list + optional localization arm + optional binding arm from an
 * Append (or bare List / bare binding). Lives here (not List) so we can use
 * `getLocalizationInfoFromExpression` without a List ↔ localization cycle.
 */
export type AppendExpressionParts = {
  staticPart: Expression;
  localizationPart: Expression | null;
  bindingPart: Expression | null;
};

export function splitAppendExpressionParts(
  expr: Expression
): AppendExpressionParts {
  const append = deconstructAppend(expr);
  return {
    staticPart: append.find(Expressions.isList) ?? EList([]),
    localizationPart: append.find(getLocalizationInfoFromExpression) ?? null,
    bindingPart:
      append.find(
        (part) =>
          Expressions.isBinding(part) &&
          !getLocalizationInfoFromExpression(part)
      ) ?? null,
  };
}

/**
 * Rebuilds a plain List or Append from static / localization / binding arms.
 */
export function rebuildAppendExpression({
  staticPart,
  localizationPart,
  bindingPart,
}: AppendExpressionParts): Expression {
  if (bindingPart && localizationPart) {
    return constructAppend([staticPart, localizationPart, bindingPart]);
  }
  if (bindingPart) {
    return constructAppend([staticPart, bindingPart]);
  }
  if (localizationPart) {
    return constructAppend([staticPart, localizationPart]);
  }
  return staticPart;
}

export function getLocalizationBindingAtExpressionPath(
  element: ExpressionElement,
  path: ExpressionPath
): LocalizationBindingData | null {
  const data = Expressions.getElementData(element);
  const value = Expressions.getIn(data, path);
  if (!value) {
    return null;
  }

  return (
    getLocalizationInfoFromExpression(value) ??
    getLocalizationInfoFromAppend(value)
  );
}

export function getLocalizationKeyInElementData(
  element: ExpressionElement,
  path: ExpressionPath
): string | null {
  return getLocalizationBindingAtExpressionPath(element, path)?.key ?? null;
}

/** Rebinds localization bindings across an element subtree. */
export function updateLocalizationBindingsInElementSubtree(
  rootElement: ExpressionElement,
  updateBinding: (
    binding: LocalizationBindingData
  ) => LocalizationBindingData | null
): ExpressionElement {
  const updated = Expressions.everywhere((expr) => {
    const existingBinding = getLocalizationBindingFromSelect(expr);
    if (!existingBinding) {
      return expr;
    }

    const nextBinding = updateBinding(existingBinding);
    if (!nextBinding) return expr;

    if (
      nextBinding.key === existingBinding.key &&
      nextBinding.componentId === existingBinding.componentId
    ) {
      return expr;
    }

    return createLocalizationBinding(nextBinding.key, nextBinding.componentId);
  })(EElement(rootElement));

  return Expressions.getElement(updated) ?? rootElement;
}

export function createSubtreeFromLocalizationValue(
  sourceElement: ExpressionElement,
  localizationValue: Expression
): ExpressionElement {
  return Expressions.setElementData(
    sourceElement,
    Expressions.setIn(
      Expressions.getElementData(sourceElement),
      CHILDREN_PATH,
      localizationValue
    )
  );
}

export const createLocalizationBindingWithScope = (
  componentType: Qualified<Component.ComponentName>,
  localizationKey: string,
  scope: LocalizationScope
) => {
  const binding = createLocalizationBinding(
    localizationKey,
    getName(componentType)
  );
  return call(binding, scopeToArgs(scope));
};

export function applyLocalizationBindingToElement({
  localizationKey,
  element,
  componentType,
  scope,
}: {
  localizationKey: string;
  element: ExpressionElement;
  componentType: Qualified<Component.ComponentName>;
  scope: LocalizationScope;
}): ExpressionElement {
  return updateElementData((data) =>
    Expressions.updateIn(
      data,
      CHILDREN_PATH,
      constant(
        createLocalizationBindingWithScope(
          componentType,
          localizationKey,
          scope
        )
      )
    )
  )(element);
}

export function findElementOwningChildrenLocalizationKey(
  root: Expression,
  localizationKey: string
): ExpressionElement | null {
  const path = Expressions.findPath(root, (expr) => {
    const el = Expressions.getElement(expr);
    if (!el) return false;
    return (
      getLocalizationKeyInElementData(el, CHILDREN_PATH) === localizationKey
    );
  });
  if (!path) return null;
  const found = Expressions.getIn(root, path);
  if (!found) return null;
  return Expressions.getElement(found);
}
